import { motion } from "framer-motion";
import { AlertTriangle, Eye, Send, Database } from "lucide-react";

const threats = [
  {
    icon: Eye,
    label: "READ",
    title: "Agent Reads Sensitive Data",
    desc: "An AI agent acting on behalf of an employee accesses confidential revenue data, salary records, or strategic documents — all within its ACL permissions.",
    color: "#00E5FF",
    borderColor: "#008080",
  },
  {
    icon: Database,
    label: "PROCESS",
    title: "Data Enters Context Window",
    desc: "The sensitive data is ingested into the LLM's context. It can be summarized, analyzed, or reformulated — but the taint of confidentiality persists.",
    color: "#F59E0B",
    borderColor: "#92400E",
  },
  {
    icon: Send,
    label: "MUTATE",
    title: "Rogue Mutation / Exfiltration",
    desc: "The agent invokes an email API, writes to an external database, or triggers a webhook — exfiltrating sensitive data at machine speed. Both tools were individually authorized.",
    color: "#EF4444",
    borderColor: "#991B1B",
  },
];

const fadeUp = {
  hidden: { opacity: 0, y: 30 },
  visible: (i) => ({
    opacity: 1,
    y: 0,
    transition: { delay: i * 0.15, duration: 0.6, ease: "easeOut" },
  }),
};

export default function Problem() {
  return (
    <section
      id="problem"
      data-testid="problem-section"
      className="relative py-24 lg:py-32 bg-[#050508]"
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        {/* Section header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="mb-16"
        >
          <span className="text-xs font-mono text-[#EF4444] uppercase tracking-[0.2em] flex items-center gap-2 mb-4">
            <AlertTriangle size={14} />
            The Threat
          </span>
          <h2
            data-testid="problem-headline"
            className="font-heading text-4xl sm:text-5xl font-bold tracking-tight text-white mb-4"
          >
            Your Agents Can Go Rogue
          </h2>
          <p className="text-base text-[#94A3B8] max-w-2xl leading-relaxed">
            Existing security gates who can access what. But they're blind to 
            <span className="text-white font-medium"> what happens after</span>.
            The real danger is the sequence: a read followed by a dangerous mutation.
          </p>
        </motion.div>

        {/* Threat flow cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-0">
          {threats.map((t, i) => (
            <motion.div
              key={t.label}
              custom={i}
              variants={fadeUp}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
              data-testid={`problem-card-${t.label.toLowerCase()}`}
              className="relative bg-[#0D0F14] border border-[#1A2332] p-8 group hover:border-opacity-60 transition-all"
              style={{ borderLeftColor: t.borderColor, borderLeftWidth: i === 0 ? "1px" : undefined }}
            >
              {/* Step indicator */}
              <div className="flex items-center gap-3 mb-6">
                <div
                  className="w-10 h-10 flex items-center justify-center border"
                  style={{ borderColor: t.color, color: t.color }}
                >
                  <t.icon size={18} />
                </div>
                <span
                  className="text-xs font-mono font-bold uppercase tracking-[0.2em]"
                  style={{ color: t.color }}
                >
                  Step {i + 1}: {t.label}
                </span>
              </div>

              <h3 className="text-xl font-semibold text-white mb-3 tracking-tight">
                {t.title}
              </h3>
              <p className="text-sm text-[#94A3B8] leading-relaxed">
                {t.desc}
              </p>

              {/* Connector arrow between cards */}
              {i < threats.length - 1 && (
                <div className="hidden md:block absolute top-1/2 -right-3 z-10 -translate-y-1/2">
                  <div
                    className="w-6 h-6 rotate-45 border-t border-r"
                    style={{ borderColor: t.color }}
                  />
                </div>
              )}
            </motion.div>
          ))}
        </div>

        {/* Bottom callout */}
        <motion.div
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          transition={{ delay: 0.5 }}
          className="mt-8 border border-[#991B1B]/40 bg-[#EF4444]/5 p-6 flex items-start gap-4"
        >
          <AlertTriangle size={20} className="text-[#EF4444] shrink-0 mt-0.5" />
          <div>
            <p className="text-sm text-[#EF4444] font-semibold mb-1">
              Perimeter controls can't stop this.
            </p>
            <p className="text-sm text-[#94A3B8]">
              Both the read and the mutation are individually authorized under the agent's ACLs. 
              The danger only emerges when you analyze the full action chain. 
              Identity providers like Okta and Cisco solve "can this agent access this tool?" — 
              not "should it, given what it just read?"
            </p>
          </div>
        </motion.div>
      </div>
    </section>
  );
}
